import React, { ChangeEventHandler } from 'react';

import clsx from 'clsx';

import Label from 'ui/Label';

type Value = string | number;

type SelectInputOption = {
  value: Value;
  label: string;
};

type TextSizeType = 'text-sm' | 'text-md' | 'text-base' | 'text-lg' | 'text-xs';

type SelectInputProps = {
  id: string;
  label?: string;
  'aria-label'?: string;
  name: string;
  value?: Value;
  defaultValue?: Value;
  isReadOnly?: boolean;
  disabled?: boolean;
  options: SelectInputOption[];
  dataTestId?: string;
  dataOptionTestId?: string;
  errorMessage?: string;
  defaultOption?: string;
  textSize?: TextSizeType;
  onChange?: ChangeEventHandler<HTMLSelectElement>;
  classNames?: string;
  helpText?: string;
};

function SelectInput({
  id,
  label,
  'aria-label': ariaLabel,
  name,
  value,
  defaultValue,
  options,
  dataTestId,
  defaultOption,
  dataOptionTestId,
  isReadOnly = false,
  disabled = false,
  errorMessage = '',
  textSize = 'text-sm',
  onChange,
  classNames,
  helpText,
}: SelectInputProps) {
  const selectParentClassStack = clsx(
    { 'mt-1': !!label },
    { '!border-red': errorMessage },
    { 'cursor-not-allowed': isReadOnly },
    classNames,
  );

  return (
    <div>
      {label ? <Label htmlFor={id}>{label}</Label> : null}
      {label && helpText ? <p className="text-sm text-gray-500">{helpText}</p> : null}
      <div className={selectParentClassStack}>
        <select
          id={id}
          key={id + defaultValue + value} // Forcing a re-render when the value changes
          data-testid={dataTestId}
          name={name}
          className={clsx(
            'block',
            'w-full',
            'rounded-md cursor-pointer',
            errorMessage ? '!border-red' : 'border-gray-300',
            'py-3',
            'pl-3',
            'pr-10',
            'text-base',
            'focus:border-indigo-500',
            'focus:outline-none',
            'focus:ring-indigo-500',
            `${textSize}`,
            isReadOnly ? 'cursor-not-allowed' : null,
            disabled ? 'bg-xxLight' : 'bg-white',
          )}
          value={value}
          disabled={disabled}
          onChange={onChange}
          defaultValue={value ? undefined : defaultValue}
          aria-label={ariaLabel ? undefined : ariaLabel}
        >
          {defaultOption ? (
            <option data-testud={dataOptionTestId} value="">
              {defaultOption}
            </option>
          ) : null}

          {options.map((option, index) => (
            <option
              data-testid={dataOptionTestId}
              key={`${index}-${option.value}`}
              value={option.value}
            >
              {option.label}
            </option>
          ))}
        </select>
      </div>
      {errorMessage ? <p className="mt-2 text-red">{errorMessage}</p> : null}
    </div>
  );
}

export default SelectInput;
